/**
 * Inbox — unified email view dla MASKIL (Pipeline Inbox).
 *
 * Synchronizuje maile z 4 OVH mailboxes (admin/ada/zapytania/kontakt).
 * Auto-klasyfikuje (quote/question/objection/spam/news/other).
 * Linked entity per mail (tender/contractor/supplier).
 *
 * Features (Tier 1 MVP):
 *   - Multi-mailbox list z badges (unread count)
 *   - Filters: mailbox / classification / starred / archived
 *   - Search po subject/body/from
 *   - Email detail panel (split view lub modal)
 *   - Mark read/star/archive
 *   - Manual sync button
 *
 * Future (Tier 2-3):
 *   - Composer + templates
 *   - AI reply suggestions
 *   - Auto-link to tender/supplier via NIP/email match
 *   - Pixel tracking
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  Mail, Inbox as InboxIcon, Star, Archive, RefreshCw, Search, Filter,
  ChevronRight, FileText, MessageSquare, AlertTriangle, Sparkles,
  Trash2, Reply, Link as LinkIcon, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import Tour, { shouldShowTour } from '../components/tutorial/Tour';
import ComposerDialog from '../components/inbox/ComposerDialog';
import { PenSquare, Forward } from 'lucide-react';
import PlanLock from '../components/common/PlanLock';

const t = (lang, pl, en) => (lang === 'pl' ? pl : en);

const CLASSIFICATION_CONFIG = {
  quote:     { label_pl: 'Oferta',    label_en: 'Quote',     color: 'bg-emerald-500/15 text-emerald-300', icon: FileText },
  question:  { label_pl: 'Pytanie',   label_en: 'Question',  color: 'bg-blue-500/15 text-blue-300',       icon: MessageSquare },
  objection: { label_pl: 'Zastrzeżenie', label_en: 'Objection', color: 'bg-amber-500/15 text-amber-300',    icon: AlertTriangle },
  spam:      { label_pl: 'Spam',      label_en: 'Spam',      color: 'bg-red-500/15 text-red-300',         icon: Trash2 },
  news:      { label_pl: 'News',      label_en: 'News',      color: 'bg-zinc-500/15 text-zinc-400',       icon: Mail },
  other:     { label_pl: 'Inne',     label_en: 'Other',     color: 'bg-slate-500/15 text-slate-300',     icon: Mail },
};

export default function Inbox({ lang = 'pl', tenantId }) {
  const navigate = useNavigate();
  const [emails, setEmails] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [stats, setStats] = useState(null);
  const [mailboxes, setMailboxes] = useState([]);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [emailDetailOpen, setEmailDetailOpen] = useState(false);

  // Filters
  const [filterMailbox, setFilterMailbox] = useState('all');
  const [filterClassification, setFilterClassification] = useState('all');
  const [filterStarred, setFilterStarred] = useState(false);
  const [filterUnread, setFilterUnread] = useState(false);
  const [search, setSearch] = useState('');
  const [showTour, setShowTour] = useState(false);
  const [composerState, setComposerState] = useState({
    open: false, mode: 'new', initialEmail: null,
  });

  function openComposer(mode, initialEmail = null) {
    setComposerState({ open: true, mode, initialEmail });
  }
  function closeComposer() {
    setComposerState({ open: false, mode: 'new', initialEmail: null });
  }

  useEffect(() => {
    const tid = setTimeout(() => {
      if (shouldShowTour('inbox-overview')) setShowTour(true);
    }, 1400);
    return () => clearTimeout(tid);
  }, []);

  const loadEmails = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 100, is_archived: false };
      if (filterMailbox !== 'all') params.mailbox = filterMailbox;
      if (filterClassification !== 'all') params.classification = filterClassification;
      if (filterStarred) params.is_starred = true;
      if (filterUnread) params.is_read = false;
      if (search) params.search = search;
      const r = await api.get('/emails', { params });
      setEmails(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }, [filterMailbox, filterClassification, filterStarred, filterUnread, search]);

  async function loadStats() {
    try { setStats((await api.get('/emails/stats')).data); }
    catch (err) { console.error(err); }
  }

  async function loadMailboxes() {
    try { setMailboxes(((await api.get('/emails/mailboxes')).data || {}).mailboxes || []); }
    catch (err) { console.error(err); }
  }

  useEffect(() => { loadMailboxes(); loadStats(); }, []);
  useEffect(() => { loadEmails(); }, [loadEmails]);

  async function handleSync() {
    setSyncing(true);
    try {
      const r = await api.post('/emails/sync');
      toast.success(t(lang, `Sync: ${r.data.total_new} nowych maili`, `Sync: ${r.data.total_new} new`));
      await Promise.all([loadEmails(), loadStats()]);
    } catch (err) {
      toast.error(t(lang, 'Błąd sync', 'Sync error') + ': ' + (err.response?.data?.detail || err.message));
    }
    setSyncing(false);
  }

  async function handlePatch(emailId, updates) {
    try {
      await api.patch(`/emails/${emailId}`, updates);
      setEmails(emails.map(e => e.id === emailId ? { ...e, ...updates } : e));
      if (selectedEmail?.id === emailId) {
        setSelectedEmail({ ...selectedEmail, ...updates });
      }
      loadStats();
    } catch (err) {
      toast.error('Error');
    }
  }

  async function openEmail(email) {
    setSelectedEmail(email);
    setEmailDetailOpen(true);
    if (!email.is_read) {
      handlePatch(email.id, { is_read: true });
    }
    // Fetch full content (with body_html)
    try {
      const r = await api.get(`/emails/${email.id}`);
      setSelectedEmail(r.data);
    } catch (err) { console.error(err); }
  }

  return (
    <div className="p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}>
            <InboxIcon className="w-6 h-6 text-primary" />
            {t(lang, 'Skrzynka pocztowa', 'Inbox')}
            {stats?.unread > 0 && (
              <Badge className="bg-primary text-primary-foreground">{stats.unread} {t(lang, 'nowych', 'unread')}</Badge>
            )}
          </h1>
          <p className="text-xs text-muted-foreground mt-1">
            {mailboxes.length} {t(lang, 'mailboxes', 'mailboxes')} · {stats?.total || 0} {t(lang, 'wiadomości', 'messages')}
            {stats?.starred > 0 && ` · ${stats.starred} ★`}
          </p>
        </div>
        <div className="flex gap-2" data-tour="inbox-sync">
          <Button variant="outline" size="sm" onClick={handleSync} disabled={syncing} data-testid="inbox-sync">
            {syncing ? <RefreshCw className="w-3.5 h-3.5 mr-1 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
            {t(lang, 'Synchronizuj', 'Sync')}
          </Button>
          <Button
            variant="default"
            size="sm"
            onClick={() => openComposer('new')}
            data-testid="inbox-new-email"
            className="gap-1"
          >
            <PenSquare className="w-3.5 h-3.5" />
            {t(lang, 'Nowy mail', 'New email')}
          </Button>
        </div>
      </div>

      {/* Stats per mailbox (PRO+ for multi-mailbox) */}
      {stats?.by_mailbox && Object.keys(stats.by_mailbox).length > 0 && (
        <div className="flex flex-wrap gap-2 text-xs items-center" data-tour="inbox-mailboxes">
          <PlanLock feature="inbox_multi_mailbox" requiredPlan="pro" inline lang={lang}>
            <span />
          </PlanLock>
          <Button
            size="sm" variant={filterMailbox === 'all' ? 'default' : 'outline'}
            onClick={() => setFilterMailbox('all')}
            data-testid="inbox-filter-mailbox-all"
          >
            {t(lang, 'Wszystkie', 'All')} ({stats.total})
          </Button>
          {Object.entries(stats.by_mailbox).map(([addr, counts]) => (
            <Button
              key={addr}
              size="sm" variant={filterMailbox === addr ? 'default' : 'outline'}
              onClick={() => setFilterMailbox(addr)}
              className="gap-1.5"
            >
              <Mail className="w-3 h-3" />
              {addr.split('@')[0]} · {counts.total}
              {counts.unread > 0 && (
                <Badge variant="outline" className="ml-1 h-4 text-[10px]">{counts.unread}</Badge>
              )}
            </Button>
          ))}
        </div>
      )}

      {/* Filters */}
      <Card data-tour="inbox-filters">
        <CardContent className="p-3 flex flex-wrap gap-2 items-center">
          <div className="flex-1 min-w-[200px] relative">
            <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={t(lang, 'Szukaj w temacie, treści, nadawcy...', 'Search subject, body, sender...')}
              className="pl-7 h-8"
              data-testid="inbox-search"
            />
          </div>
          <Select value={filterClassification} onValueChange={setFilterClassification}>
            <SelectTrigger className="w-36 h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t(lang, 'Wszystkie typy', 'All types')}</SelectItem>
              {Object.entries(CLASSIFICATION_CONFIG).map(([key, cfg]) => (
                <SelectItem key={key} value={key}>
                  {lang === 'pl' ? cfg.label_pl : cfg.label_en}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            size="sm" variant={filterStarred ? 'default' : 'outline'}
            onClick={() => setFilterStarred(!filterStarred)}
            className="h-8 gap-1"
          >
            <Star className="w-3.5 h-3.5" /> Star
          </Button>
          <Button
            size="sm" variant={filterUnread ? 'default' : 'outline'}
            onClick={() => setFilterUnread(!filterUnread)}
            className="h-8"
          >
            {t(lang, 'Nieprzeczytane', 'Unread')}
          </Button>
        </CardContent>
      </Card>

      {/* Email list */}
      <Card data-tour="inbox-list">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-2">
              <Skeleton className="h-12" /><Skeleton className="h-12" /><Skeleton className="h-12" />
            </div>
          ) : emails.length === 0 ? (
            <div className="p-12 text-center text-muted-foreground">
              <InboxIcon className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p className="text-sm">{t(lang, 'Brak wiadomości spełniających kryteria.', 'No messages matching filters.')}</p>
              <p className="text-xs mt-1">{t(lang, 'Spróbuj zsynchronizować lub zmień filtry.', 'Try sync or change filters.')}</p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {emails.map(e => {
                const clsCfg = CLASSIFICATION_CONFIG[e.ai_classification] || CLASSIFICATION_CONFIG.other;
                const Icon = clsCfg.icon;
                return (
                  <div
                    key={e.id}
                    className={`p-3 flex items-center gap-3 cursor-pointer hover:bg-muted/30 transition-colors ${
                      !e.is_read ? 'bg-primary/5' : ''
                    }`}
                    onClick={() => openEmail(e)}
                    data-testid={`inbox-row-${e.id}`}
                  >
                    {/* Star */}
                    <button
                      onClick={ev => { ev.stopPropagation(); handlePatch(e.id, { is_starred: !e.is_starred }); }}
                      className="flex-shrink-0"
                    >
                      <Star className={`w-4 h-4 ${e.is_starred ? 'fill-amber-400 text-amber-400' : 'text-muted-foreground/40'}`} />
                    </button>

                    {/* From */}
                    <div className="w-48 flex-shrink-0 min-w-0">
                      <div className={`text-sm truncate ${!e.is_read ? 'font-semibold' : ''}`}>
                        {e.from_name || e.from_addr.split('@')[0]}
                      </div>
                      <div className="text-[11px] text-muted-foreground truncate">{e.from_addr}</div>
                    </div>

                    {/* Subject + preview */}
                    <div className="flex-1 min-w-0">
                      <div className={`text-sm truncate ${!e.is_read ? 'font-semibold' : ''}`}>
                        {e.subject || '(no subject)'}
                      </div>
                      <div className="text-[11px] text-muted-foreground truncate">
                        {(e.body_text || '').slice(0, 120)}
                      </div>
                    </div>

                    {/* Badges */}
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <Badge className={`text-[10px] gap-1 ${clsCfg.color}`}>
                        <Icon className="w-3 h-3" />
                        {lang === 'pl' ? clsCfg.label_pl : clsCfg.label_en}
                      </Badge>
                      {e.linked_tender_id && (
                        <Badge variant="outline" className="text-[10px] gap-1">
                          <LinkIcon className="w-3 h-3" /> {e.linked_tender_id.slice(0, 12)}
                        </Badge>
                      )}
                      {e.attachments?.length > 0 && (
                        <Badge variant="outline" className="text-[10px]">📎 {e.attachments.length}</Badge>
                      )}
                    </div>

                    {/* Mailbox + date */}
                    <div className="w-32 text-right text-[11px] text-muted-foreground flex-shrink-0">
                      <div>{e.mailbox?.split('@')[0]}</div>
                      <div>{e.date_raw?.slice(0, 16) || '—'}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Email detail dialog */}
      <Dialog open={emailDetailOpen} onOpenChange={setEmailDetailOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          {selectedEmail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {(() => {
                    const cfg = CLASSIFICATION_CONFIG[selectedEmail.ai_classification] || CLASSIFICATION_CONFIG.other;
                    const Icon = cfg.icon;
                    return (
                      <Badge className={`text-[10px] gap-1 ${cfg.color}`}>
                        <Icon className="w-3 h-3" />
                        {lang === 'pl' ? cfg.label_pl : cfg.label_en}
                        {selectedEmail.ai_confidence ? ` ${Math.round(selectedEmail.ai_confidence * 100)}%` : ''}
                      </Badge>
                    );
                  })()}
                  <span className="text-base">{selectedEmail.subject || '(no subject)'}</span>
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-3">
                {/* Headers */}
                <div className="text-xs space-y-1 p-3 bg-muted/20 rounded">
                  <div><span className="text-muted-foreground">Od:</span> <strong>{selectedEmail.from_name}</strong> &lt;{selectedEmail.from_addr}&gt;</div>
                  <div><span className="text-muted-foreground">Do:</span> {selectedEmail.to_addr} <Badge variant="outline" className="ml-1 text-[10px]">{selectedEmail.mailbox}</Badge></div>
                  {selectedEmail.cc && <div><span className="text-muted-foreground">CC:</span> {selectedEmail.cc}</div>}
                  <div><span className="text-muted-foreground">Data:</span> {selectedEmail.date_raw}</div>
                </div>

                {/* Actions */}
                <div className="flex gap-2 flex-wrap">
                  <Button size="sm" variant="outline" onClick={() => handlePatch(selectedEmail.id, { is_starred: !selectedEmail.is_starred })}>
                    <Star className={`w-3.5 h-3.5 mr-1 ${selectedEmail.is_starred ? 'fill-amber-400 text-amber-400' : ''}`} />
                    {selectedEmail.is_starred ? t(lang, 'Odgwiazdkuj', 'Unstar') : t(lang, 'Gwiazdka', 'Star')}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => { handlePatch(selectedEmail.id, { is_archived: true }); setEmailDetailOpen(false); toast.success(t(lang, 'Zarchiwizowano', 'Archived')); }}>
                    <Archive className="w-3.5 h-3.5 mr-1" />
                    {t(lang, 'Archiwizuj', 'Archive')}
                  </Button>
                  <Button
                    size="sm"
                    variant="default"
                    onClick={() => { setEmailDetailOpen(false); openComposer('reply', selectedEmail); }}
                    data-testid="reply-from-detail"
                  >
                    <Reply className="w-3.5 h-3.5 mr-1" />
                    {t(lang, 'Odpowiedz', 'Reply')}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => { setEmailDetailOpen(false); openComposer('forward', selectedEmail); }}
                    data-testid="forward-from-detail"
                  >
                    <Forward className="w-3.5 h-3.5 mr-1" />
                    {t(lang, 'Przekaż', 'Forward')}
                  </Button>
                  <Button size="sm" variant="outline" disabled title={t(lang, 'AI link w przyszłej wersji', 'AI link coming soon')}>
                    <LinkIcon className="w-3.5 h-3.5 mr-1" />
                    {t(lang, 'Linkuj do przetargu', 'Link to tender')}
                  </Button>
                </div>

                {/* Body */}
                <div className="border border-border rounded p-4 max-h-96 overflow-y-auto">
                  {selectedEmail.body_html ? (
                    <div className="prose prose-sm max-w-none dark:prose-invert"
                         dangerouslySetInnerHTML={{ __html: selectedEmail.body_html }} />
                  ) : (
                    <pre className="text-sm whitespace-pre-wrap font-sans">{selectedEmail.body_text}</pre>
                  )}
                </div>

                {/* Attachments */}
                {selectedEmail.attachments?.length > 0 && (
                  <div className="space-y-1">
                    <div className="text-xs text-muted-foreground">{t(lang, 'Załączniki', 'Attachments')}:</div>
                    {selectedEmail.attachments.map((a, i) => (
                      <Badge key={i} variant="outline" className="text-[10px]">
                        📎 {a.filename} ({Math.round((a.size_bytes || 0) / 1024)} KB)
                      </Badge>
                    ))}
                  </div>
                )}

                {/* Linked entities */}
                {(selectedEmail.linked_tender_id || selectedEmail.linked_supplier_id) && (
                  <div className="p-2 bg-primary/5 border border-primary/20 rounded text-xs">
                    {t(lang, 'Powiązane:', 'Linked:')}
                    {selectedEmail.linked_tender_id && (
                      <Button size="sm" variant="link" onClick={() => navigate(`/tenders/${selectedEmail.linked_tender_id}`)}>
                        <ExternalLink className="w-3 h-3 mr-1" />Tender {selectedEmail.linked_tender_id}
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <ComposerDialog
        open={composerState.open}
        onClose={closeComposer}
        mode={composerState.mode}
        initialEmail={composerState.initialEmail}
        linkedTenderId={composerState.initialEmail?.linked_tender_id || null}
        linkedSupplierId={composerState.initialEmail?.linked_supplier_id || null}
        lang={lang}
        onSent={async () => {
          // Refresh inbox so user sees the original marked as is_replied
          await loadEmails();
          await loadStats();
        }}
      />

      <Tour
        tourId="inbox-overview"
        lang={lang}
        active={showTour}
        onComplete={() => setShowTour(false)}
        onSkip={() => setShowTour(false)}
        steps={[
          {
            target: '[data-tour="inbox-sync"]',
            title: lang === 'pl' ? 'Synchronizacja maili' : 'Email sync',
            content: lang === 'pl'
              ? 'Klik „Synchronizuj" pobiera nowe maile z 4 skrzynek OVH (admin/ada/zapytania/kontakt). Cron robi to co 5 min w tle — możesz forsować ręcznie.'
              : 'Click "Sync" to pull new emails from 4 OVH mailboxes (admin/ada/zapytania/kontakt). Cron does it every 5 min — force manually if needed.',
            placement: 'left',
          },
          {
            target: '[data-tour="inbox-mailboxes"]',
            title: lang === 'pl' ? 'Wiele skrzynek pocztowych' : 'Multiple mailboxes',
            content: lang === 'pl'
              ? 'Każda rola ma osobny adres: admin@ (logowanie), ada@ (FROM dla wysyłek), zapytania@ (inbox dla ofert), kontakt@ (publiczny). Filtruj klikając.'
              : 'Each role has its own address: admin@ (login), ada@ (sending FROM), zapytania@ (inbox for quotes), kontakt@ (public). Filter by clicking.',
            placement: 'bottom',
          },
          {
            target: '[data-tour="inbox-filters"]',
            title: lang === 'pl' ? 'Klasyfikacja AI + szukaj' : 'AI classification + search',
            content: lang === 'pl'
              ? 'AI klasyfikuje przychodzące: oferta / pytanie / zastrzeżenie / spam / news. Filtruj po typie, gwiazdce, nieprzeczytanych — albo wyszukuj fulltext.'
              : 'AI classifies incoming: quote / question / objection / spam / news. Filter by type, star, unread — or fulltext search.',
            placement: 'bottom',
          },
          {
            target: '[data-tour="inbox-list"]',
            title: lang === 'pl' ? 'Pipeline-linked maile' : 'Pipeline-linked emails',
            content: lang === 'pl'
              ? 'Każdy mail może być powiązany z przetargiem lub dostawcą — badge „🔗 Tender..." prowadzi 1 klik do kontekstu. Tu zamykasz pętlę „dostawca odpisał → werdykt się odświeży".'
              : 'Each email can be linked to a tender or supplier — the "🔗 Tender..." badge opens that context in one click. This closes the loop "supplier replied → verdict refreshes".',
            placement: 'top',
          },
        ]}
      />
    </div>
  );
}
